import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import loadScript from './loadScript';
import styles from './LocationInput.module.css';
import inputStyles from '../shared/Input.module.css';
import CountryDropdown from '../CountryDropdown';

type Address = {
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
}

interface Props {
  label?: string,
  values: Address,
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  setNewValues: (key: string, value: string) => void,
  showCountry: boolean,
  showAddress?: boolean,
  state: string,
  hint?: string,
  unrequired?: string[],
  dataCy?: string,
}

export default function LocationInput({
  label, values, handleChange, setNewValues, showCountry, showAddress = true,
  state, hint, unrequired = [], dataCy,
}: Props) {
  let autoComplete: any;
  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handlePlaceSelect = async () => {
    const addressObject = autoComplete.getPlace();
    const newAddress = `${addressObject.address_components.find((details: any) => details.types.includes('street_number'))?.long_name
      || addressObject.address_components.find((details: any) => details.types.includes('political'))?.long_name
      || addressObject.address_components.find((details: any) => details.types.includes('locality'))?.long_name
      || values.address
      } ${addressObject.address_components.find((details: any) => details.types.includes('route'))?.long_name || ''}`.trim();
    setNewValues('address', newAddress);

    setNewValues('zipCode', addressObject.address_components.find((details: any) => details.types.includes('postal_code'))?.long_name);
    setNewValues('city', addressObject.address_components.find((details: any) => details.types.includes('locality'))?.long_name);
    setNewValues('country', addressObject.address_components.find((details: any) => details.types.includes('country'))?.short_name);
  };

  const handleScriptLoad = () => {
    if (autoCompleteRef.current) {
      autoComplete = new window.google.maps.places.Autocomplete(
        autoCompleteRef.current
      );
      autoComplete.setFields(['address_components', 'formatted_address']);
      autoComplete.addListener('place_changed', () => handlePlaceSelect());
    }
  };

  useEffect(() => {
    loadScript(
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyDQyUNkEbrMMvJY-X0F6gx4y5B2KLv__U4&libraries=places',
      handleScriptLoad,
    );
  }, []);

  return (
    <div className={`${(state === 'ERROR') ? styles.error : ''} ${styles.container}`}>

      {
        (showAddress)
          ? (
            <div className={styles.inputContainer}>
              <p className={inputStyles.label}>
                {label || t('coordonnate.address')}
              </p>
              <input
                className={inputStyles.input}
                name="address"
                ref={autoCompleteRef}
                onChange={handleChange}
                placeholder={t('coordonnate.address')}
                value={values.address || ''}
                maxLength={256}
                data-cy={dataCy}
              />
            </div>
          )
          : (
            <div className={styles.inputContainer}>
              <p className={inputStyles.label}>
                {label || t('coordonnate.city')}
              </p>
              <input
                type="text"
                name="city"
                ref={(!showAddress) ? autoCompleteRef : null}
                className={inputStyles.input}
                placeholder={t('coordonnate.city')}
                value={values.city || ''}
                onChange={handleChange}
                maxLength={64}
                data-cy={dataCy}
              />
            </div>
          )
      }
      <div className={styles.row}>
        <div className={styles.inputContainer}>
          <p className={inputStyles.label}>
            {t('coordonnate.zipCode')}
            {
              (unrequired.includes('zipCode'))
              && (
                <span className={styles.unrequired}>
                  {`(${t('coordonnate.unrequired')})`}
                </span>
              )
            }
          </p>
          <input
            type="text"
            name="zipCode"
            className={inputStyles.input}
            placeholder={t('coordonnate.zipCode')}
            value={values.zipCode || ''}
            onChange={handleChange}
            maxLength={64}
            data-cy={`${dataCy}-zipCode`}
          />
        </div>
        {
          (showAddress)
          && (
            <div className={styles.inputContainer}>
              <p className={inputStyles.label}>
                {t('coordonnate.city')}
              </p>
              <input
                type="text"
                name="city"
                className={inputStyles.input}
                placeholder={t('coordonnate.city')}
                value={values.city || ''}
                onChange={handleChange}
                maxLength={64}
                data-cy={`${dataCy}-city`}
              />
            </div>
          )
        }
        {
          (showCountry)
          && (
            <CountryDropdown
              label={t('coordonnate.country')}
              type="text"
              name="country"
              className={inputStyles.input}
              labelClassName={inputStyles.label}
              containerClassName={styles.inputContainer}
              placeholder={t('coordonnate.country')}
              value={values.country || ''}
              onChange={handleChange}
              maxLength={64}
              dataCy={`${dataCy}-country`}
            />
          )
        }
      </div>
      {
        (hint)
        && (
          <p className={styles.hint}>
            {hint}
          </p>
        )
      }
    </div>
  );
}
